import React, { useRef, useEffect, useState } from "react";
import Text from "@/components/ui/text";
import Input from "@/components/ui/input";

interface EditableViewNameProps {
  view: {
    id: string;
    name: string;
    isSystem: boolean;
  };
  i18n: {
    exists: (key: string) => boolean;
    t: (key: string) => string;
  };
  onRename: (view: any, newName: string) => void;
  isEditing?: boolean;
  onEditComplete?: () => void;
}

const NewEditableViewName: React.FC<EditableViewNameProps> = ({
  view,
  i18n,
  onRename,
  isEditing = false,
  onEditComplete
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState(view.name);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      // Move cursor to end of text
      const length = inputRef.current.value.length;
      inputRef.current.setSelectionRange(length, length);
    }
  }, [isEditing]);

  useEffect(() => {
    setInputValue(view.name);
  }, [view.name]);

  const handleSave = () => {
    const trimmedValue = inputValue.trim();
    if (trimmedValue && trimmedValue !== view.name) {
      onRename(view, trimmedValue);
    } else {
      setInputValue(view.name);
    }
    onEditComplete?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      setInputValue(view.name);
      onEditComplete?.();
    }
  };

  const commonClassName = "h-6 flex items-center";

  if (view.isSystem) {
    return (
      <div className={commonClassName}>
        <Text variant="body">
          {i18n.exists(`datatoolbar.presets.${view.name}`)
            ? i18n.t(`datatoolbar.presets.${view.name}`)
            : view.name}
        </Text>
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className={`${commonClassName} -ml-1 w-full`}>
        <Input
          ref={inputRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          maxLength={100}
          className="h-7 py-0 px-2 text-base w-full"
          containerClassName="w-full"
        />
      </div>
    );
  }

  return (
    <div className={commonClassName}>
      <Text variant="body" className="truncate">
        {view.name}
      </Text>
    </div>
  );
};

export default NewEditableViewName;
